import React, { useCallback, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RotateCcwIcon, PencilIcon } from 'lucide-react';
interface MemberCard3DProps {
  defaultName?: string;
  customerId?: string;
}
export function MemberCard3D({
  defaultName = '',
  customerId
}: MemberCard3DProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [name, setName] = useState(defaultName);
  const [tilt, setTilt] = useState({
    x: 0,
    y: 0
  });
  const [lightPos, setLightPos] = useState({
    x: 50,
    y: 50
  });
  const [isHovering, setIsHovering] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const generatedId = useMemo(() => {
    if (customerId) return customerId;
    const rand = Math.floor(1000 + Math.random() * 9000);
    return `MHP-2025-${rand}`;
  }, [customerId]);
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const offsetX = (e.clientX - centerX) / (rect.width / 2);
    const offsetY = (e.clientY - centerY) / (rect.height / 2);
    setTilt({
      x: -offsetY * 12,
      y: offsetX * 12
    });
    const pctX = (e.clientX - rect.left) / rect.width * 100;
    const pctY = (e.clientY - rect.top) / rect.height * 100;
    setLightPos({
      x: pctX,
      y: pctY
    });
  }, []);
  const handleMouseEnter = useCallback(() => setIsHovering(true), []);
  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    setTilt({
      x: 0,
      y: 0
    });
    setLightPos({
      x: 50,
      y: 50
    });
  }, []);
  const handleFlip = useCallback(() => {
    setIsFlipped(f => !f);
  }, []);
  const displayName = name || 'TÊN CỦA BẠN';
  return <div className="flex flex-col items-center gap-4" data-id="element-212">
      {/* Card container — tilt only, no click */}
      <div ref={cardRef} className="w-full max-w-[420px]" style={{
      perspective: '1200px'
    }} onMouseMove={handleMouseMove} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} data-id="element-213">
        <motion.div className="relative w-full" style={{
        aspectRatio: '85.6 / 54',
        transformStyle: 'preserve-3d'
      }} animate={{
        rotateX: isHovering ? tilt.x : 0,
        rotateY: isFlipped ? isHovering ? 180 + tilt.y : 180 : isHovering ? tilt.y : 0
      }} transition={{
        type: 'spring',
        stiffness: isHovering ? 200 : 80,
        damping: 20,
        mass: 0.8
      }} data-id="element-214">
          {/* ===== FRONT FACE ===== */}
          <div className="absolute inset-0 rounded-2xl overflow-hidden" style={{
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden'
        }} data-id="element-215">
            <div className="absolute inset-0" style={{
            background: 'linear-gradient(135deg, #8B7340 0%, #B8963E 15%, #D4B85A 30%, #C8A84E 50%, #B8963E 70%, #A08050 85%, #8B7340 100%)'
          }} data-id="element-216" />
            <div className="absolute inset-0 opacity-[0.08]" style={{
            backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(255,255,255,0.3) 1px, rgba(255,255,255,0.3) 2px)`,
            backgroundSize: '3px 100%'
          }} data-id="element-217" />
            <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: `repeating-linear-gradient(135deg, transparent, transparent 2px, rgba(0,0,0,0.2) 2px, rgba(0,0,0,0.2) 3px)`,
            backgroundSize: '4px 4px'
          }} data-id="element-218" />
            <div className="absolute inset-0 pointer-events-none transition-opacity duration-300" style={{
            background: `radial-gradient(ellipse at ${lightPos.x}% ${lightPos.y}%, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.05) 35%, transparent 65%)`,
            opacity: isHovering ? 1 : 0
          }} data-id="element-219" />
            <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), inset 0 -1px 0 rgba(0,0,0,0.2), inset 1px 0 0 rgba(255,255,255,0.1), inset -1px 0 0 rgba(0,0,0,0.1)'
          }} data-id="element-220" />

            <div className="relative h-full flex flex-col justify-between p-5 md:p-6" data-id="element-221">
              <div className="flex items-start justify-between" data-id="element-222">
                <div className="w-10 h-8 md:w-12 md:h-9 rounded-md relative overflow-hidden" style={{
                background: 'linear-gradient(145deg, #D4B85A, #A08050)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.3)'
              }} data-id="element-223">
                  <div className="absolute inset-0 flex flex-col justify-center px-1.5 gap-[2px]" data-id="element-224">
                    <div className="h-[1px] bg-[#8B7340]/50" data-id="element-225" />
                    <div className="h-[1px] bg-[#8B7340]/50" data-id="element-226" />
                    <div className="h-[1px] bg-[#8B7340]/50" data-id="element-227" />
                    <div className="h-[1px] bg-[#8B7340]/50" data-id="element-228" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center" data-id="element-229">
                    <div className="w-[1px] h-full bg-[#8B7340]/40" data-id="element-230" />
                    <div className="w-3 md:w-4" data-id="element-231" />
                    <div className="w-[1px] h-full bg-[#8B7340]/40" data-id="element-232" />
                  </div>
                </div>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="opacity-50 md:w-7 md:h-7" style={{
                transform: 'rotate(90deg)'
              }} data-id="element-233">
                  <path d="M12 6C14.2 6 16.2 6.9 17.6 8.4" stroke="#6B5B30" strokeWidth="1.5" strokeLinecap="round" data-id="element-234" />
                  <path d="M12 10C13.1 10 14.1 10.5 14.8 11.2" stroke="#6B5B30" strokeWidth="1.5" strokeLinecap="round" data-id="element-235" />
                  <path d="M12 2C16.4 2 20.2 3.8 22.8 6.8" stroke="#6B5B30" strokeWidth="1.5" strokeLinecap="round" data-id="element-236" />
                </svg>
              </div>

              <div className="flex flex-col items-center -mt-2" data-id="element-237">
                <span className="font-display text-4xl md:text-5xl font-bold tracking-wide" style={{
                color: '#8B7340',
                textShadow: '1px 1px 1px rgba(255,255,255,0.35), -1px -1px 1px rgba(60,45,15,0.5), 0 0 2px rgba(139,115,64,0.3)'
              }} data-id="element-238">
                  MHP
                </span>
                <span className="text-[10px] md:text-xs font-semibold tracking-[0.25em] uppercase mt-0.5" style={{
                color: '#7A6535',
                textShadow: '0.5px 0.5px 0.5px rgba(255,255,255,0.25), -0.5px -0.5px 0.5px rgba(60,45,15,0.4)'
              }} data-id="element-239">
                  Mỹ Hòa Phát
                </span>
              </div>

              <div className="flex items-end justify-between" data-id="element-240">
                <span className="text-[7px] md:text-[8px] font-medium tracking-[0.3em] uppercase" style={{
                color: '#7A6535',
                textShadow: '0.5px 0.5px 0.5px rgba(255,255,255,0.2), -0.5px -0.5px 0.5px rgba(60,45,15,0.3)'
              }} data-id="element-241">
                  Khách Hàng Thân Thiết
                </span>
                <span className="text-[7px] md:text-[8px] font-medium tracking-wider uppercase" style={{
                color: '#7A6535',
                textShadow: '0.5px 0.5px 0.5px rgba(255,255,255,0.2), -0.5px -0.5px 0.5px rgba(60,45,15,0.3)'
              }} data-id="element-242">
                  PREMIUM
                </span>
              </div>
            </div>

            <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
            boxShadow: '0 25px 50px -12px rgba(139,115,64,0.25), 0 12px 24px -8px rgba(0,0,0,0.3)'
          }} data-id="element-243" />
          </div>

          {/* ===== BACK FACE ===== */}
          <div className="absolute inset-0 rounded-2xl overflow-hidden" style={{
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          transform: 'rotateY(180deg)'
        }} data-id="element-244">
            <div className="absolute inset-0" style={{
            background: 'linear-gradient(135deg, #6B5B30 0%, #8B7340 25%, #A08050 50%, #8B7340 75%, #6B5B30 100%)'
          }} data-id="element-245" />
            <div className="absolute inset-0 opacity-[0.06]" style={{
            backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(255,255,255,0.3) 1px, rgba(255,255,255,0.3) 2px)`,
            backgroundSize: '3px 100%'
          }} data-id="element-246" />
            <div className="absolute inset-0 pointer-events-none transition-opacity duration-300" style={{
            background: `radial-gradient(ellipse at ${100 - lightPos.x}% ${lightPos.y}%, rgba(255,255,255,0.2) 0%, transparent 55%)`,
            opacity: isHovering ? 1 : 0
          }} data-id="element-247" />
            <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -1px 0 rgba(0,0,0,0.2)'
          }} data-id="element-248" />

            {/* Back face content — name as TEXT only, no input */}
            <div className="relative h-full flex flex-col" data-id="element-249">
              <div className="mt-5 md:mt-6 w-full h-9 md:h-10" style={{
              background: 'linear-gradient(180deg, #1A1500 0%, #2A2200 50%, #1A1500 100%)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }} data-id="element-250" />

              <div className="flex-1 px-5 md:px-6 pt-4 md:pt-5 flex flex-col" data-id="element-251">
                <div className="flex-1" data-id="element-252">
                  <p className="text-[7px] md:text-[8px] uppercase tracking-[0.2em] mb-1.5 font-medium" style={{
                  color: '#5A4D2A'
                }} data-id="element-253">
                    Tên Chủ Thẻ
                  </p>
                  <div className="rounded-md px-3 py-1.5 md:py-2 mb-3 md:mb-4 min-h-[32px] md:min-h-[36px] flex items-center" style={{
                  background: 'linear-gradient(135deg, #F5F0E0, #EDE5D0)',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
                }} data-id="element-254">
                    <span className={`text-sm md:text-base font-semibold ${name ? '' : 'opacity-50'}`} style={{
                    color: name ? '#2A2200' : '#B0A080'
                  }} data-id="element-255">
                      {displayName}
                    </span>
                  </div>

                  <p className="text-[7px] md:text-[8px] uppercase tracking-[0.2em] mb-1 font-medium" style={{
                  color: '#5A4D2A'
                }} data-id="element-256">
                    Mã Khách Hàng
                  </p>
                  <p className="text-xs md:text-sm font-mono font-bold tracking-wider" style={{
                  color: '#4A3F20',
                  textShadow: '0.5px 0.5px 0px rgba(255,255,255,0.2)'
                }} data-id="element-257">
                    {generatedId}
                  </p>
                </div>

                <div className="flex items-end justify-between pb-4 md:pb-5" data-id="element-258">
                  <span className="text-[7px] md:text-[8px] font-medium" style={{
                  color: '#5A4D2A'
                }} data-id="element-259">
                    Có hiệu lực 12 tháng
                  </span>
                  <span className="text-[7px] md:text-[8px] font-medium tracking-wider" style={{
                  color: '#5A4D2A',
                  textShadow: '0.5px 0.5px 0px rgba(255,255,255,0.15)'
                }} data-id="element-260">
                    myhoaphat.com
                  </span>
                </div>
              </div>
            </div>

            <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{
            boxShadow: '0 25px 50px -12px rgba(107,91,48,0.25), 0 12px 24px -8px rgba(0,0,0,0.3)'
          }} data-id="element-261" />
          </div>
        </motion.div>
      </div>

      {/* Name input — OUTSIDE the 3D context, only visible when flipped */}
      <AnimatePresence data-id="element-262">
        {isFlipped && <motion.div initial={{
        opacity: 0,
        y: -8,
        height: 0
      }} animate={{
        opacity: 1,
        y: 0,
        height: 'auto'
      }} exit={{
        opacity: 0,
        y: -8,
        height: 0
      }} transition={{
        duration: 0.25
      }} className="w-full max-w-[420px] overflow-hidden" data-id="element-263">
            <div className="relative" data-id="element-264">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-265">
                <PencilIcon className="w-4 h-4 text-[#C8A84E]" data-id="element-266" />
              </div>
              <input ref={inputRef} type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Nhập tên khắc trên thẻ..." className="w-full pl-10 pr-4 py-3 bg-[#1A1A1A] border border-[#C8A84E]/30 rounded-xl text-sm text-white placeholder:text-[#666666] focus:outline-none focus:border-[#C8A84E] focus:ring-1 focus:ring-[#C8A84E]/30 transition-all" maxLength={30} aria-label="Tên khắc trên thẻ" data-id="element-267" />
              {name && <button onClick={() => setName('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666666] hover:text-white transition-colors text-xs" aria-label="Xóa tên" data-id="element-268">
                  ✕
                </button>}
            </div>
            <p className="text-[10px] text-[#666666] mt-1.5 text-center" data-id="element-269">
              Tên sẽ được khắc trên thẻ kim loại của bạn
            </p>
          </motion.div>}
      </AnimatePresence>

      {/* Flip button */}
      <motion.button onClick={handleFlip} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-[#B0B0B0] hover:text-[#C8A84E] bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#C8A84E]/30 transition-colors" whileTap={{
      scale: 0.95
    }} data-id="element-270">
        <RotateCcwIcon className="w-4 h-4" data-id="element-271" />
        {isFlipped ? 'Xem mặt trước' : 'Lật xem mặt sau & nhập tên'}
      </motion.button>
    </div>;
}